import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { User } from 'avn/models/user';
import { BusinessMainService } from 'avn/services/business-main.service';
import { Constants } from 'avn/services/constants';
import { UserService } from 'avn/services/user.service';
import { WorkforceTokenService } from 'avn/services/workforce-token.service';
import { ProfilePicturePromptComponent } from 'avn/shared/components/profile-picture-prompt/profile-picture-prompt';
import { SharedCompsService } from 'avn/shared/shared-comps.service';
import { EMPTY, Observable, catchError, map, of, switchMap, tap, throwError } from 'rxjs';

import { AuthAppService } from './auth-app.service';

interface AuthCommonNavigationOptions {
  isMobile: boolean;
  redirectUrl?: string;
  promptDelayMs?: number;
}

@Injectable({
  providedIn: 'root'
})
export class AuthCommonService {
  constructor(
    private userService: UserService,
    private authService: AuthAppService,
    private businessMainService: BusinessMainService,
    private dialog: MatDialog,
    private router: Router,
    private snackBar: SharedCompsService,
    private workforceTokenService: WorkforceTokenService
  ) {}

  authenticate(credentials: { email: string; password: string }): Observable<User> {
    return this.userService.login(credentials).pipe(
      switchMap((response: any) => {
        try {
          this.authService.setAuth(response?.auth_token);
          return this.loadAuthenticatedUserContext();
        } catch (error: any) {
          this.snackBar.showSnackBar('There has been an error in authenticating your account. Please try again!');
          const normalizedError: any =
            error instanceof HttpErrorResponse ? error : error?.rejection || error?.originalError || error;
          Sentry.captureException(`Auth error ::: ${normalizedError}`);
          this.authService.purgeAuth();
          return throwError(() => error);
        }
      }),
      catchError((error) => {
        const errormsg =
          error?.error?.message || 'There has been an error in authenticating your account. Please try again';
        this.snackBar.showSnackBar(errormsg);
        this.authService.purgeAuth();
        return throwError(() => error);
      })
    );
  }

  loadAuthenticatedUserContext(): Observable<User> {
    return this.userService.getUserDetail().pipe(
      switchMap((user: User) =>
        this.userService.getConfigData().pipe(
          tap((response: any) => Constants.setRuntimeConfig(response)),
          map(() => user),
          catchError(() => {
            return of(user);
          })
        )
      ),
      switchMap((user: User) => {
        if (!this.requiresBusinessContext(user)) {
          this.authService.setloggedUser(user);
          return of(user);
        }

        return this.userService
          .getBusinessLocationList()
          .pipe(map((response: any) => this.applyBusinessContext(user, response)));
      })
    );
  }

  handlePostLogin(user: User, options: AuthCommonNavigationOptions) {
    this.getBusinessDashboardDetail();

    this.navigateToPage(user, options);

    if (this.authService.getViewType() !== 'practitioner') {
      return;
    }

    const openPrompt = () => this.checkPractitionerProfilePicture(user?.header_detail?.uuid);
    if (options.promptDelayMs) {
      setTimeout(() => openPrompt(), options.promptDelayMs);
      return;
    }

    openPrompt();
  }

  getBusinessDashboardDetail() {
    return this.userService
      .getDashboardBusinessDetail()
      .pipe(
        tap((response: any) => this.workforceTokenService.setDashboardDetail(response)),
        switchMap((response: any) =>
          this.workforceTokenService.isWorkforceEnabled(response) ? this.workforceTokenService.getToken() : EMPTY
        )
      )
      .subscribe({
        error: (err: any) => {
          console.info(err);
        }
      });
  }

  redirectToWellbeingDashboard(user: User): boolean {
    if (!user?.is_wellbeing_owner && !user?.is_wellbeing_employees) {
      return false;
    }

    const role = user.is_wellbeing_owner ? 'employer' : 'employee';
    const token = this.authService.getToken();
    if (!token) {
      return false;
    }

    const url = `${Constants.WELLBEING_DASHBOARD_URL}?r=${role}&s=${token}`;
    window.location.href = url;
    return true;
  }

  private navigateToPage(user: User, options: AuthCommonNavigationOptions) {
    if (this.redirectToWellbeingDashboard(user)) {
      return;
    }

    if (
      !options.isMobile &&
      (user.is_owner || user.is_group_owner || user.is_practitioner) &&
      user?.businesses?.length > 0 &&
      user?.header_detail?.uuid &&
      !options.redirectUrl?.includes('booking/')
    ) {
      this.router.navigate([`business/${user.header_detail.uuid}`]);
      return;
    }

    if (options.redirectUrl) {
      this.router.navigate([options.redirectUrl]);
      return;
    }

    this.router.navigate(['/']);
  }

  private requiresBusinessContext(user: User): boolean {
    return !!(user?.is_group_owner || user?.is_owner || user?.is_practitioner);
  }

  private applyBusinessContext(user: User, response: any): User {
    if (user?.is_owner || user?.is_group_owner) {
      user.businesses = response.owner;
      user.header_detail = response.owner[0];
      this.authService.setViewType('owner');
      this.authService.setBusinessUUID(user.header_detail.uuid);
    } else if (user?.is_practitioner) {
      user.businesses = response.practitioner;
      user.header_detail = response.practitioner[0];
      this.authService.setViewType('practitioner');
      this.authService.setBusinessUUID(user.header_detail.uuid);
    }

    if (response.wellbeing) {
      this.authService.setWellbeingDetails(response.wellbeing);
    }

    this.authService.setloggedUser(user);
    return user;
  }

  private checkPractitionerProfilePicture(bizUuid: string) {
    if (!bizUuid) {
      return;
    }

    const params = { pageCount: 10, currentPage: 1, status: 'Active' };

    this.businessMainService.getAllBusinessPractitioners(bizUuid, params).subscribe({
      next: (response) => {
        const list = response?.data ?? [];
        if (!list.length) {
          return;
        }

        const practitioner = list[0];
        const isMissing = practitioner?.default_correct_image === true;
        const name = practitioner?.name ?? '';
        const practitionerUuid = practitioner?.uuid ?? '';

        if (!isMissing || !name || !practitionerUuid) {
          return;
        }

        const dialogData: MatDialogConfig = {
          disableClose: false,
          hasBackdrop: true,
          width: '100%',
          maxWidth: '500px',
          panelClass: ['promptPanel', 'popup-container'],
          backdropClass: 'questionareBD',
          data: {
            practitioners: [name]
          }
        };

        const dialogRef = this.dialog.open(ProfilePicturePromptComponent, dialogData);
        dialogRef.afterClosed().subscribe((result: any) => {
          if (result === 'upload') {
            this.router.navigate([`business/${bizUuid}/practitioners/edit/${practitionerUuid}`]);
          }
        });
      },
      error: () => {
        //ignore
      }
    });
  }
}
